import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Box,
  IconButton
} from '@mui/material';

import {
  BlockOutlined,
  EditOutlined,
  HistoryOutlined
} from '@mui/icons-material';

import {
  patients,
  users
} from './data';

import PageHeaderTabs
  from '../Common/components/PageHeaderTabs';

import PatientSettings
  from './components/PatientSettings';

import UserSettings
  from './components/UserSettings';

import InvitePatientModal
  from './components/InvitePatientModal';

import InviteUserModal
  from './components/UserSettings/InviteUserModal';

import OrganizationProfile
  from './components/OrganizationProfile';

import PersonalProfile
  from './components/PersonalProfile';

import {
  settingsTabs
} from './settingsConfig';

import {
  patientPageContentSx,
  patientPageRootSx
} from '../Common/components/Styles/PatientPageStyles';

const actionButtonSx = {
  p: 0.5,
  borderRadius: '50%',
  color: '#757575',

  '&:hover': {
    bgcolor: '#F3F3F3'
  }
};

const Settings = () => {
  const [tab, setTab] = useState(0);

  const [
    patientSearch,
    setPatientSearch
  ] = useState('');

  const [
    userSearch,
    setUserSearch
  ] = useState('');

  const [
    patientStatus,
    setPatientStatus
  ] = useState('All');

  const [
    userStatus,
    setUserStatus
  ] = useState('All');

  const [account, setAccount] =
    useState('All');

  const [
    invitePatientOpen,
    setInvitePatientOpen
  ] = useState(false);

  const [
    inviteUserOpen,
    setInviteUserOpen
  ] = useState(false);

  const [loading, setLoading] =
    useState(true);

  const [
    patientPage,
    setPatientPage
  ] = useState(0);

  const [
    patientRowsPerPage,
    setPatientRowsPerPage
  ] = useState(25);

  const [
    userPage,
    setUserPage
  ] = useState(0);

  const [
    userRowsPerPage,
    setUserRowsPerPage
  ] = useState(25);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () =>
      clearTimeout(timeout);
  }, []);

  const filteredPatients =
    useMemo(() => {
      const searchValue =
        patientSearch
          .trim()
          .toLowerCase();

      return patients.filter(row => {
        const searchMatch =
          !searchValue ||
          row.patient
            ?.toLowerCase()
            .includes(searchValue) ||
          row.email
            ?.toLowerCase()
            .includes(searchValue) ||
          row.mobile
            ?.toLowerCase()
            .includes(searchValue);

        const statusMatch =
          patientStatus === 'All' ||
          row.status === patientStatus;

        return (
          searchMatch &&
          statusMatch
        );
      });
    }, [
      patientSearch,
      patientStatus
    ]);

  const filteredUsers =
    useMemo(() => {
      const searchValue =
        userSearch
          .trim()
          .toLowerCase();

      return users.filter(row => {
        const searchMatch =
          !searchValue ||
          row.name
            ?.toLowerCase()
            .includes(searchValue) ||
          row.username
            ?.toLowerCase()
            .includes(searchValue);

        const statusMatch =
          userStatus === 'All' ||
          row.status === userStatus;

        const accountMatch =
          account === 'All' ||
          row.account === account ||
          row.role === account ||
          (
            account === 'Provider' &&
            row.provider === true
          ) ||
          (
            account === 'Staff' &&
            row.staff === true
          ) ||
          (
            account === 'Admin' &&
            row.admin === true
          );

        return (
          searchMatch &&
          statusMatch &&
          accountMatch
        );
      });
    }, [
      userSearch,
      userStatus,
      account
    ]);

  const visiblePatients =
    useMemo(() => {
      const startIndex =
        patientPage *
        patientRowsPerPage;

      return filteredPatients.slice(
        startIndex,
        startIndex +
          patientRowsPerPage
      );
    }, [
      filteredPatients,
      patientPage,
      patientRowsPerPage
    ]);

  const visibleUsers =
    useMemo(() => {
      const startIndex =
        userPage *
        userRowsPerPage;

      return filteredUsers.slice(
        startIndex,
        startIndex +
          userRowsPerPage
      );
    }, [
      filteredUsers,
      userPage,
      userRowsPerPage
    ]);

  const renderPatientActions =
    row => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5
        }}>
        <IconButton
          size="small"
          sx={actionButtonSx}>
          <EditOutlined
            sx={{ fontSize: 18 }}
          />
        </IconButton>

        <IconButton
          size="small"
          sx={actionButtonSx}>
          {row.status ===
          'Active' ? (
            <BlockOutlined
              sx={{ fontSize: 18 }}
            />
          ) : (
            <HistoryOutlined
              sx={{ fontSize: 18 }}
            />
          )}
        </IconButton>
      </Box>
    );

  const renderUserActions =
    row => (
      <IconButton
        size="small"
        sx={actionButtonSx}>
        {row.status === 'Active' ? (
          <BlockOutlined
            sx={{ fontSize: 18 }}
          />
        ) : (
          <HistoryOutlined
            sx={{ fontSize: 18 }}
          />
        )}
      </IconButton>
    );

  return (
    <Box sx={patientPageRootSx}>
      <PageHeaderTabs
        type="tabs"
        value={tab}
        tabs={settingsTabs}
        onChange={(
          event,
          newValue
        ) =>
          setTab(newValue)
        }
      />

      <Box
        sx={
          patientPageContentSx
        }>
        {tab === 0 && (
          <PatientSettings
            search={patientSearch}
            status={patientStatus}
            rows={visiblePatients}
            totalCount={
              filteredPatients.length
            }
            page={patientPage}
            rowsPerPage={
              patientRowsPerPage
            }
            onSearchChange={event => {
              setPatientSearch(
                event.target.value
              );
              setPatientPage(0);
            }}
            onStatusChange={event => {
              setPatientStatus(
                event.target.value
              );
              setPatientPage(0);
            }}
            onPageChange={(
              event,
              newPage
            ) =>
              setPatientPage(
                newPage
              )
            }
            onRowsPerPageChange={
              event => {
                setPatientRowsPerPage(
                  Number(
                    event.target.value
                  )
                );
                setPatientPage(0);
              }
            }
            onInvite={() =>
              setInvitePatientOpen(
                true
              )
            }
            renderAction={
              renderPatientActions
            }
          />
        )}

        {tab === 1 && (
          <UserSettings
            search={userSearch}
            status={userStatus}
            account={account}
            rows={visibleUsers}
            totalCount={
              filteredUsers.length
            }
            page={userPage}
            rowsPerPage={
              userRowsPerPage
            }
            loading={loading}
            onSearchChange={event => {
              setUserSearch(
                event.target.value
              );
              setUserPage(0);
            }}
            onStatusChange={event => {
              setUserStatus(
                event.target.value
              );
              setUserPage(0);
            }}
            onAccountChange={
              event => {
                setAccount(
                  event.target.value
                );
                setUserPage(0);
              }
            }
            onPageChange={(
              event,
              newPage
            ) =>
              setUserPage(newPage)
            }
            onRowsPerPageChange={
              event => {
                setUserRowsPerPage(
                  Number(
                    event.target.value
                  )
                );
                setUserPage(0);
              }
            }
            onInvite={() =>
              setInviteUserOpen(true)
            }
            renderAction={
              renderUserActions
            }
          />
        )}

        {tab === 2 && (
          <OrganizationProfile />
        )}

        {tab === 3 && (
          <PersonalProfile />
        )}
      </Box>

      <InvitePatientModal
        open={
          invitePatientOpen
        }
        onClose={() =>
          setInvitePatientOpen(
            false
          )
        }
      />

      <InviteUserModal
        open={inviteUserOpen}
        onClose={() =>
          setInviteUserOpen(
            false
          )
        }
      />
    </Box>
  );
};

export default Settings;